import React from 'react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { getSeoProps } from '@spa/offer/utils/seo';
import { mapPhotos } from '@spa/shared/utils/photos';
import { getRedirect } from '@spa/offer/utils/redirect';
import OfferView from '@spa/offer/containers/Offer/View';
import { similarOffers } from '@spa/shared/clients/search';
import { getEulerianData } from '@spa/offer/utils/eulerian';
import { getPropsFromReq } from '@spa/shared/utils/request';
import { normalizeQuery } from '@spa/shared/utils/queryParams';
import { getOfferAggregateById } from '@spa/shared/clients/graphql';
import { buildPriceDetails } from '@spa/offer/utils/transformers/priceDetails';
import { AUTHENTICATION_HEADER, getIdFromCookie, isGuestUnauthenticated } from '@spa/shared/utils/user';
import { buildSimilarOffersParams } from '@spa/offer/utils/transformers/similarOffers';
import { OFFER_RECOMMENDATIONS_PER_RENT_TYPE } from '@spa/offer/constants/recommendations';
import {
  addAuthentication,
  fetchGuest,
  getLocation,
  pricingRequest,
  getGuestPaidBookingRequests
} from '@spa/shared/clients/core';

const OfferPage = props => <OfferView {...props} />;

export const getServerSideProps = async ({ locale = 'en', req, res, query, query: { offerId } }) => {
  res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59');

  const languagePath = locale === 'en' ? '' : `${locale}/`;

  try {
    const authCookie = req.cookies[AUTHENTICATION_HEADER];
    const guestId = getIdFromCookie(req.cookies);
    const normalizedQuery = normalizeQuery(query);

    if (authCookie) {
      addAuthentication(authCookie);
    }

    const [offerAggregate, guest, translations] = await Promise.all([
      getOfferAggregateById(offerId),
      authCookie && guestId ? fetchGuest(guestId) : Promise.resolve(null),
      serverSideTranslations(locale, ['header', 'footer', 'translations'])
    ]);

    const redirect = getRedirect(query, req, offerAggregate, languagePath);
    if (redirect) {
      return redirect;
    }

    const hasPriceDetails = !!(normalizedQuery.moveIn && normalizedQuery.moveOut) || !!normalizedQuery.optionId;
    const priceDetailsRequest = buildPriceDetails({ offerAggregate, query: normalizedQuery, guest });
    const priceDetailsData = hasPriceDetails ? await pricingRequest(priceDetailsRequest).catch(() => null) : null;

    const paidBookings = guestId ? await getGuestPaidBookingRequests(guestId).catch(() => []) : [];

    const {
      property_aggregate: {
        property: {
          floors: [{ units }],
          location: {
            address: { city_code }
          },
          lifecycle: { rent_by: rentType }
        }
      },
      accommodation_offer: { main_photo: mainPhoto },
      accommodation_provider: { id: accommodationProviderId },
      photos
    } = offerAggregate;

    const [recommendationsByAccommodationProvider, recommendationsByOffer, location] = await Promise.all([
      similarOffers(
        buildSimilarOffersParams(
          offerId,
          city_code,
          normalizedQuery,
          accommodationProviderId,
          null,
          OFFER_RECOMMENDATIONS_PER_RENT_TYPE[rentType],
          locale
        )
      ).catch(() => ({ data: [] })),
      similarOffers(
        buildSimilarOffersParams(
          offerId,
          city_code,
          normalizedQuery,
          null,
          accommodationProviderId,
          OFFER_RECOMMENDATIONS_PER_RENT_TYPE[rentType],
          locale
        )
      ).catch(() => ({
        data: []
      })),
      getLocation({ ...query }).catch(() => null)
    ]);

    const propsFromReq = getPropsFromReq(req);

    return {
      props: {
        initialState: {
          page: propsFromReq,
          profile: {
            ...guest,
            ...(authCookie ? { token: authCookie } : {}),
            paidBookings,
            isLoggedIn: !!authCookie && !isGuestUnauthenticated(authCookie),
            wish_list: { ...(guest?.wish_list || {}), loading: false }
          },
          offer: {
            offerAggregate,
            photosModal: {
              photos: mapPhotos(units, photos, mainPhoto, true)
            },
            priceDetails: {
              isLoading: false,
              request: priceDetailsRequest,
              data: priceDetailsData
            },
            recommendations: {
              byAccommodationProvider: recommendationsByAccommodationProvider,
              byOffer: recommendationsByOffer
            },
            tracking: {
              referrer: req.headers.referer || null
            },
            isCalendarOpen: false
          },
          location
        },
        ...(process.env.EULERIAN_URL
          ? {
              eulerianProps: {
                path: req.url,
                ...getEulerianData(offerAggregate, guest, authCookie, locale)
              }
            }
          : {}),
        ...translations,
        ...propsFromReq,
        seoProps: getSeoProps({ offerAggregate, locale, languagePath })
      }
    };
  } catch (error) {
    console.error(`${offerId} - ${error.message}`);

    const [, queryPath = null] = req.url.split('?');
    const unpublishedOfferQueryParam = `unpublishedOfferId=${offerId}`;
    const queryString = queryPath ? `?${queryPath}&${unpublishedOfferQueryParam}` : `?${unpublishedOfferQueryParam}`;

    return {
      redirect: {
        destination: `${process.env.APP_URL}${languagePath}accommodation/${query.city}${queryString}`,
        statusCode: 307
      }
    };
  }
};

export default OfferPage;
